import { Box, Typography, Stack, Paper } from "@mui/material";

function Section({ title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "18px",
        border: "1px solid #e5e7eb",
        p: { xs: 2.5, md: 3.5 },
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          color: "primary.main",
          fontSize: "1.2rem",
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Stack spacing={1}>{children}</Stack>
    </Paper>
  );
}

function Item({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "0.98rem",
        lineHeight: 1.7,
      }}
    >
      • {children}
    </Typography>
  );
}

function Text({ children, mb = 0 }) {
  return (
    <Typography
      sx={{
        fontSize: "0.98rem",
        lineHeight: 1.7,
        mb,
      }}
    >
      {children}
    </Typography>
  );
}

function SubTitle({ children }) {
  return (
    <Typography
      sx={{
        fontWeight: 700,
        color: "text.primary",
        fontSize: "1rem",
        mt: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function RulesEnglish() {
  return (
    <Stack spacing={3}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "1.4rem",
          color: "primary.main",
        }}
      >
        Appendix C: Basketball
      </Typography>

      <Typography sx={{ fontWeight: 700 }}>
        FIBA (latest edition)
      </Typography>

      <Section title="Rule Exceptions">
        <Item>Players do not have to wear the same color socks.</Item>
      </Section>

      <Section title="Playing Time">
        <Text mb={0.5}>
          Warm-up time: Pre-game and half-time periods on the tournament court are reserved solely for participating teams dressed in proper uniforms.
        </Text>

        <Item>4 x 8 quarters for a game - stopped clock.</Item>
        <Item>10-min. half-time.</Item>
        <Item>2 minutes in between quarters.</Item>
        <Item>8 seconds to get the ball over mid court.</Item>
        <Item>24 second shot clock.</Item>
        <Item>
          Overtime periods are half the time of the normal quarter – 4 minutes for tournament games.
        </Item>
        <Item>
          Time will not stop in the last 2 minutes if one of the teams is ahead by 25 points or more. The same rules will apply overtime.
        </Item>
        <Item>
          The referee will call teams to start after 7 minutes of the 10-minute warm-up period.
        </Item>
        <Item>FIBA timeout rules apply.</Item>
        <Item>
          A team leading by 25 or more points is not allowed to full-court press.
        </Item>
      </Section>

      <Section title="Rosters">
        <Text>
          The SAAC Handbook outlines the roster limits for each respective sport, for basketball the maximum roster limit is 10 boys and 10 girls. A minimum of 7 players is needed to be eligible to participate in SAAC sanctioned basketball tournaments. Rosters are to be submitted two weeks prior to the tournament.
        </Text>
      </Section>

      <Section title="Substitution">
        <Text>As allowed under FIBA rule.</Text>
      </Section>

      <Section title="Equipment">
        <Text>
          Boys will use the size 7 basketball and girls will use the size 6 basketball.
        </Text>
      </Section>

      <Section title="Facilities">
        <Text>
          At least two full-size basketball courts with an electronic scoreboard is required.
        </Text>
        <Text>
          It is recommended that schools hosting basketball have visible 24-second shot clocks.
        </Text>
      </Section>

      <Section title="Uniforms">
        <Text>All teams must have light and dark uniforms.</Text>
      </Section>

      <Section title="Officials">
        <Text>
          The source of officials is to be determined by the host school. Whenever possible, the host school should use host country international sporting organizations to officiate tournaments.
        </Text>
        <Text>
          Basketball: 2-3 per table + 6 refs to rotate (4 on, 2 off) *3 crews of officials.
        </Text>
      </Section>

      <Section title="Classifications and Records">
        <Text mb={0.5}>
          Teams shall be classified according to their won-loss records using the following system:
        </Text>

        <Item>Two (2) points for each game won.</Item>
        <Item>One (1) point for each game lost.</Item>
        <Item>Zero (0) points for a game lost by forfeit.</Item>
      </Section>

      <Section title="Round-Robin Tie Breaker">
        <Text mb={0.5}>
          SAAC will use the official FIBA Rules for Classification of Teams.
        </Text>

        <SubTitle>FIBA Tie-Breaker 2020 (6/21 - Language included)</SubTitle>

        <Text>
          Teams shall be classified according to their win-loss record, namely 2 classification points for each game won, 1 classification point for each game lost (including lost by default) and 0 classification point for a game lost by forfeit.
        </Text>

        <Text>
          D.1.2 The procedure is to be applied for all competitions with a round-robin system.
        </Text>

        <Text>
          D.1.3 If 2 or more teams have the same win-loss record of all games in the group, the game(s) between these 2 or more teams shall decide on the classification. If these 2 or more teams have the same win-loss record of the games between them, further criteria shall be applied in the following order:
        </Text>

        <Item>
          Higher game points difference between the games between them.
        </Item>
        <Item>
          Higher number of game points scored in the games between them.
        </Item>
        <Item>
          Higher game points difference between the games between common opponents.
        </Item>
        <Item>
          Higher number of game points scored in the games between common opponents.
        </Item>
        <Item>
          Higher game points difference of all games in the group.
        </Item>
        <Item>
          Higher number of game points scored in all games in the group.
        </Item>

        <Text>
          If still tied before all games have been played in the group, tied teams shall share the same ranking. If these criteria still cannot decide at the end of the group phase, a draw shall decide on the final classification.
        </Text>

        <Text>
          D.1.4 If at any level of these criteria one or more team(s) are already classified, the procedure of D.1.3 shall be repeated from the beginning for all the remaining teams not classified yet.
        </Text>

        <SubTitle>
          If the tied teams have not all played each other in the round robin
        </SubTitle>

        <Item>
          Higher game points difference between the games between common opponents.
        </Item>
        <Item>
          Higher number of game points scored in the games between common opponents.
        </Item>
        <Item>
          Higher game points difference of all games in the group.
        </Item>
        <Item>
          Higher number of game points scored in all games in the group.
        </Item>

        <Text>
          If still tied before all games have been played in the group, tied teams shall share the same ranking. If these criteria still cannot decide at the end of the group phase, a draw shall decide on the final classification.
        </Text>

        <Text>
          D.1.4 If at any level of these criteria one or more team(s) are already classified, the procedure of D.1.3 shall be repeated from the beginning for all the remaining teams not classified yet.
        </Text>
      </Section>
    </Stack>
  );
}

export default RulesEnglish;